import { stockApi } from '@/features/stock/api';
import { uploadImage } from '@/lib/images/upload';
import type { ParsedImport, ParsedRow } from './parseImportZip';

export interface ImportProgress {
  phase: 'uploadingImages' | 'writingStock' | 'done';
  imagesDone: number;
  imagesTotal: number;
  rowsDone: number;
  rowsTotal: number;
}

export interface ImportRowResult {
  rowIndex: number;
  name: string;
  outcome: 'created' | 'updated' | 'failed';
  errorMessage?: string;
}

export interface ImportSummary {
  created: number;
  updated: number;
  failed: number;
  rows: ImportRowResult[];
}

export interface RunImportOptions {
  parsed: ParsedImport;
  companyId: number | undefined;
  onProgress?: (p: ImportProgress) => void;
}

const UPLOAD_CONCURRENCY = 4;

async function runWithConcurrency<T, R>(
  items: T[],
  worker: (item: T, index: number) => Promise<R>,
  concurrency: number,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  async function take(): Promise<void> {
    while (true) {
      const i = next++;
      if (i >= items.length) return;
      results[i] = await worker(items[i], i);
    }
  }
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, take);
  await Promise.all(workers);
  return results;
}

export async function runImport({
  parsed,
  companyId,
  onProgress,
}: RunImportOptions): Promise<ImportSummary> {
  const rowsToProcess = parsed.rows.filter((r) => r.errors.length === 0);

  const filenamesToUpload = new Set<string>();
  for (const row of rowsToProcess) {
    if (row.imageFilename) filenamesToUpload.add(row.imageFilename.toLowerCase());
    if (row.logoFilename) filenamesToUpload.add(row.logoFilename.toLowerCase());
  }
  const uploadList = Array.from(filenamesToUpload);

  let imagesDone = 0;
  const emit = (overrides: Partial<ImportProgress> = {}) =>
    onProgress?.({
      phase: 'uploadingImages',
      imagesDone,
      imagesTotal: uploadList.length,
      rowsDone: 0,
      rowsTotal: rowsToProcess.length,
      ...overrides,
    });

  emit();

  const uploadedUrls = new Map<string, string>();
  await runWithConcurrency(
    uploadList,
    async (filename) => {
      const blob = parsed.imageBlobs.get(filename);
      if (!blob) {
        imagesDone++;
        emit();
        return;
      }
      const ext = filename.split('.').pop() || 'png';
      const mimeGuess = blob.type || guessMime(ext);
      const file = new File([blob], filename, { type: mimeGuess });
      try {
        const res = await uploadImage(file, { compress: false });
        uploadedUrls.set(filename, res.url);
      } finally {
        imagesDone++;
        emit();
      }
    },
    UPLOAD_CONCURRENCY,
  );

  const summary: ImportSummary = { created: 0, updated: 0, failed: 0, rows: [] };
  let rowsDone = 0;

  emit({ phase: 'writingStock', rowsDone, rowsTotal: rowsToProcess.length });

  for (const row of rowsToProcess) {
    const result = await writeRow(row, uploadedUrls, companyId);
    summary.rows.push(result);
    if (result.outcome === 'created') summary.created++;
    else if (result.outcome === 'updated') summary.updated++;
    else summary.failed++;
    rowsDone++;
    emit({ phase: 'writingStock', rowsDone, rowsTotal: rowsToProcess.length });
  }

  emit({
    phase: 'done',
    rowsDone: rowsToProcess.length,
    rowsTotal: rowsToProcess.length,
  });

  return summary;
}

async function writeRow(
  row: ParsedRow,
  uploadedUrls: Map<string, string>,
  companyId: number | undefined,
): Promise<ImportRowResult> {
  const payload = { ...row.payload };
  if (row.imageFilename) {
    payload.image = uploadedUrls.get(row.imageFilename.toLowerCase()) ?? payload.image;
  }
  if (row.logoFilename) {
    payload.logo = uploadedUrls.get(row.logoFilename.toLowerCase()) ?? payload.logo;
  }

  try {
    if (row.duplicateOf && row.decision === 'replace') {
      await stockApi.update(row.duplicateOf.id, payload);
      return { rowIndex: row.rowIndex, name: payload.name, outcome: 'updated' };
    }
    await stockApi.create({ ...payload, companyId });
    return { rowIndex: row.rowIndex, name: payload.name, outcome: 'created' };
  } catch (err) {
    return {
      rowIndex: row.rowIndex,
      name: payload.name,
      outcome: 'failed',
      errorMessage: err instanceof Error ? err.message : String(err),
    };
  }
}

function guessMime(ext: string): string {
  const e = ext.toLowerCase();
  if (e === 'jpg' || e === 'jpeg') return 'image/jpeg';
  if (e === 'png') return 'image/png';
  if (e === 'webp') return 'image/webp';
  if (e === 'svg') return 'image/svg+xml';
  return 'application/octet-stream';
}
